import { Box, Button, Stack, Typography } from "@mui/material";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import CallIcon from "@mui/icons-material/Call";
import AlternateEmailIcon from "@mui/icons-material/AlternateEmail";
import useClaimCandidate from "hooks/useClaimCandidate";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { canAccess } from "services/Permissions.service";
import { PERMISSION_KEYS } from "constants/roles";

export default function ExistingCandidateCard({ candidate , setIsDialogOpen = null}) {
    const roleId = useSelector((state) => state.userValues.user?.role.id);
    const shouldNavigate = canAccess(PERMISSION_KEYS.CANDIDATE_VIEW_ALREADY_PARSED_CANDIDATES, roleId);
    const { email, id, hasRecruitmentProcess, linkedin, phone } = candidate;
    const { claimCandidate, isLoading, isSuccess } = useClaimCandidate();
    const navigate = useNavigate();

    const goToCandidatePage = (candidateId) => {
        if (!candidateId) return;
        navigate("/candidate/view/" + candidateId);
        if (setIsDialogOpen) {
            setIsDialogOpen(false);
        }
    };

    if (!candidate) return null;

    return (
        <>
            <Stack
                spacing={2}
                direction={"row"}
                justifyContent="space-between"
                alignItems="center"
                sx={{
                    padding: 2,
                    boxShadow:
                        "rgba(60, 64, 67, 0.3) 0px 1px 2px 0px, rgba(60, 64, 67, 0.15) 0px 1px 3px 1px",
                    borderRadius: 2,
                    width: "100%",
                }}
            >
                <Stack
                    direction="row"
                    gap={1}
                    alignItems="center"
                    sx={{
                        position: "relative",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                    }}
                >
                    {id && (
                        <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            sx={{
                                padding: "2px 4px",
                                borderRadius: "4px",
                                backgroundColor: "gray.light",
                            }}
                        >
                            <Typography variant="body2" color="textSecondary">
                                ID:
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{ fontWeight: "bold" }}
                            >
                                #{id}
                            </Typography>
                        </Stack>
                    )}
                    {email && (
                        <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            component={"a"}
                            href={`mailto:${email}`}
                            sx={{
                                cursor: "pointer",
                                "&:hover": {
                                    "> .email-icon": {
                                        backgroundColor: "#a8e3f8",
                                    },
                                    "> .text": {
                                        textDecoration: "underline",
                                        color: "#0297CB",
                                    },
                                },
                            }}
                        >
                            <Stack
                                direction="row"
                                className="email-icon"
                                spacing={0.5}
                                alignItems="center"
                                sx={{
                                    padding: "2px 4px",
                                    borderRadius: "4px",
                                    backgroundColor: "#ddf6ff",
                                }}
                            >
                                <AlternateEmailIcon
                                    fontSize="10"
                                    color="cyan"
                                />
                            </Stack>
                            <Typography className="text" variant="body2">
                                {email}
                            </Typography>
                        </Stack>
                    )}
                    {phone && (
                        <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            component={"a"}
                            href={`tel:${phone}`}
                            sx={{
                                cursor: "pointer",
                                "&:hover": {
                                    "> .tel-icon": {
                                        backgroundColor: "#bcffc0",
                                    },
                                    "> .text": {
                                        textDecoration: "underline",
                                        color: "#55E59E",
                                    },
                                },
                            }}
                        >
                            <Stack
                                direction="row"
                                className="tel-icon"
                                spacing={0.5}
                                alignItems="center"
                                sx={{
                                    padding: "2px 4px",
                                    borderRadius: "4px",
                                    backgroundColor: "#d3ecdf",
                                }}
                            >
                                <CallIcon
                                    fontSize="10"
                                    sx={{ color: "#55E59E" }}
                                />
                            </Stack>

                            <Typography className="text" variant="body2">
                                {phone}
                            </Typography>
                        </Stack>
                    )}
                    {!email && !phone && (
                        <Typography variant="body2" color="textSecondary">
                            No contact information available
                        </Typography>
                    )}
                </Stack>
                <Stack direction="row" spacing={2} alignItems="center">
                    {linkedin && (
                        <Box
                            component="a"
                            href={`https://www.linkedin.com/in/${linkedin}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                                "&:hover": {
                                    "> svg": {
                                        color: "#a8e3f8",
                                    },
                                },
                            }}
                        >
                            <LinkedInIcon color="primary" />
                        </Box>
                    )}
                    {!isSuccess && (
                        <>
                            {hasRecruitmentProcess ? (
                                <Typography
                                    variant="body2"
                                    color="error.main"
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        backgroundColor: "#ffdada",
                                        padding: "4px 8px",
                                        borderRadius: "4px",
                                        fontWeight: "bold",
                                    }}
                                >
                                    Already in recruitment process
                                </Typography>
                            ) : (
                                shouldNavigate ? (
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        size="small"
                                        onClick={() => goToCandidatePage(id)}
                                    >
                                        View candidate
                                    </Button>
                                ) : 
                                <Button
                                    variant="contained"
                                    color="primary"
                                    size="small"
                                    onClick={() => claimCandidate(id)}
                                    disabled={isLoading || isSuccess}
                                >
                                    Claim candidate
                                </Button>

                            )}
                        </>
                    )}
                    {isSuccess && (
                        <Button
                            variant="body2"
                            color="success.main"
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                backgroundColor: "#d4edda",
                                padding: "4px 8px",
                                borderRadius: "4px",
                                fontWeight: "bold",
                            }}
                            onClick={() => goToCandidatePage(id)}
                        >
                            Go to profile
                        </Button>
                    )}
                </Stack>
            </Stack>
        </>
    );
}
